import React, { useState, useEffect } from 'react';
import { standingsService } from '../services/standingsService';
import { useSeasonContext } from '../context/SeasonContext';
import '../styles/Rankings.css';
import '../pages/Pages.css';

function Rankings() {
  const [standings, setStandings] = useState([]);
  const [conferences, setConferences] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { selectedSeason, setSelectedSeason, availableSeasons } = useSeasonContext();

  useEffect(() => {
    loadConferences();
  }, []);

  useEffect(() => {
    setActiveTab('all');
  }, [selectedSeason]);

  useEffect(() => {
    if (selectedSeason) {
      loadStandings();
    }
  }, [selectedSeason, activeTab]);

  const loadStandings = async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      if (activeTab === 'all') {
        data = await standingsService.getStandingsBySeason(selectedSeason.id);
      } else {
        const conf = conferences.find(c => c.abbreviation === activeTab);
        if (conf) {
          data = await standingsService.getStandingsByConference(selectedSeason.id, conf.id);
        } else {
          data = [];
        }
      }
      const rankedData = sortAndRankStandings(data);
      setStandings(rankedData);
    } catch (err) {
      console.error('Error loading standings:', err);
      setError('Failed to load standings');
    } finally {
      setLoading(false);
    }
  };

  const loadConferences = async () => {
    try {
      const confData = await standingsService.getConferences();
      setConferences(confData);
    } catch (err) {
      console.error('Error loading conferences:', err);
    }
  };

  const calculatePointDifferential = (team) => {
    const diff = team.points_for - team.points_against;
    return diff > 0 ? `+${diff}` : `${diff}`;
  };

  const calculateWinPercentage = (team) => {
    if (team.wins + team.losses === 0) return '0.000';
    const pct = team.wins / (team.wins + team.losses);
    return pct.toFixed(3);
  };

  const sortAndRankStandings = (teams) => {
    const sorted = [...teams].sort((a, b) => {
      const aGamesPlayed = a.wins + a.losses;
      const bGamesPlayed = b.wins + b.losses;
      
      const aDiff = a.points_for - a.points_against;
      const bDiff = b.points_for - b.points_against;

      if (aGamesPlayed === 0 && bGamesPlayed === 0) {
        return 0;
      }
      
      if (aGamesPlayed === 0) return 1;
      if (bGamesPlayed === 0) return -1;

      if (a.wins !== b.wins) {
        return b.wins - a.wins;
      }

      if (a.losses !== b.losses) {
        return a.losses - b.losses;
      }

      if (bDiff !== aDiff) {
        return bDiff - aDiff;
      }

      return b.points_for - a.points_for;
    });

    return sorted.map((team, index) => ({
      ...team,
      calculatedRank: index + 1,
    }));
  };

  return (
    <div className="page">
      <div className="rankings-header">
        <h1>League Standings</h1>
        <p className="rankings-subtitle">Current team rankings and records</p>
      </div>

      <div className="standings-season-selector">
        <label htmlFor="ranking-season-select">Select Season:</label>
        <select
          id="ranking-season-select"
          value={selectedSeason ? String(selectedSeason.id) : ''}
          onChange={(e) => setSelectedSeason(Number(e.target.value))}
        >
          {availableSeasons.map(season => (
            <option key={season.id} value={String(season.id)}>
              {season.name} {season.is_active ? '(Current)' : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="standings-tabs">
        <button 
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All Teams
        </button>
        {conferences.map(conf => (
          <button 
            key={conf.id}
            className={`tab ${activeTab === conf.abbreviation ? 'active' : ''}`}
            onClick={() => setActiveTab(conf.abbreviation)}
          >
            {conf.name}
          </button>
        ))}
      </div>

      {loading && (
        <div className="standings-loading" data-testid="standings-loading">
          <div className="spinner"></div>
          <p>Loading standings...</p>
        </div>
      )}

      {error && (
        <div className="standings-error" data-testid="standings-error">
          <p>{error}</p>
        </div>
      )}

      {!loading && standings.length > 0 && (
        <div className="standings-container">
          <div className="standings-header-row">
            <div className="standings-title">
              {activeTab === 'all' && 'All Teams Standings'}
              {activeTab !== 'all' && `${conferences.find(c => c.abbreviation === activeTab)?.name} Standings`}
            </div>
          </div>

          <div className="standings-table-wrapper">
            <table className="standings-table">
              <thead>
                <tr>
                  <th className="col-rank">RNKP</th>
                  <th className="col-team">TEAM</th>
                  <th className="col-stat">W</th>
                  <th className="col-stat">L</th>
                  <th className="col-stat">PCT</th>
                  <th className="col-stat">PF</th>
                  <th className="col-stat">PA</th>
                  <th className="col-diff">DIFF</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((team, index) => {
                  const diff = team.points_for - team.points_against;
                  const isPositive = diff >= 0;
                  return (
                    <tr key={team.id} className={index % 2 === 0 ? 'even' : 'odd'}>
                      <td className="col-rank">
                        {team.calculatedRank === 1 && <span className="trophy">🏆</span>}
                        {team.calculatedRank}
                      </td>
                      <td className="col-team">
                        <div className="team-info">
                          {team.teams.logo_url ? (
                            <img src={team.teams.logo_url} alt={team.teams.name} className="team-logo team-logo-image" />
                          ) : (
                            <span className="team-logo">{team.teams.abbreviation.charAt(0)}</span>
                          )}
                          <div className="team-details">
                            <span className="team-name">{team.teams.name}</span>
                            <span className="team-abbr">{team.teams.abbreviation}</span>
                          </div>
                        </div>
                      </td>
                      <td className="col-stat stat-wins">{team.wins}</td>
                      <td className="col-stat stat-losses">{team.losses}</td>
                      <td className="col-stat">{calculateWinPercentage(team)}</td>
                      <td className="col-stat">{team.points_for}</td>
                      <td className="col-stat">{team.points_against}</td>
                      <td className={`col-diff ${isPositive ? 'positive' : 'negative'}`}>
                        <span className="diff-arrow">{isPositive ? '↗' : '↘'}</span>
                        {calculatePointDifferential(team)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="standings-legend">
            <div className="legend-row">
              <div className="legend-item">
                <strong>W:</strong> <span>Wins</span>
              </div>
              <div className="legend-item">
                <strong>L:</strong> <span>Losses</span>
              </div>
              <div className="legend-item">
                <strong>PCT:</strong> <span>Win Percentage</span>
              </div>
            </div>
            <div className="legend-row">
              <div className="legend-item">
                <strong>PF:</strong> <span>Points For</span>
              </div>
              <div className="legend-item">
                <strong>PA:</strong> <span>Points Against</span>
              </div>
              <div className="legend-item">
                <strong>DIFF:</strong> <span>Point Differential</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && standings.length === 0 && !error && (
        <div className="standings-empty" data-testid="standings-empty">
          <p>No standings data available for this selection.</p>
        </div>
      )}
    </div>
  );
}

export default Rankings;
